import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Train, LogOut, Plus, RefreshCw, Calendar, Trash2,
  ChevronDown, Clock, MapPin, Users, AlertCircle,
  CheckCircle, MinusCircle, XCircle, LayoutGrid, Pencil, Save,
  Wallet, RotateCcw, Trash, Archive, Settings, Send, Radio
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { API } from "@/App";

// Dashboard sub-components — legacy + new Pro Dashboard
import {
  MockDataBanner,
  Sidebar,
  TopBar,
  KpiStrip,
  PnrCard,
  FilterRail,
  LiveOpsPanel,
  TrendCard,
  ActivityFeed,
  ApiHealthCard,
  WalletPassesCard,
  NextDeparture,
  QuickAddPnr,
  GreetingBlock,
  DashboardFooter,
} from "@/components/dashboard";
import PNRFilters from "@/components/dashboard/PNRFilters";
import LiveTrainStatus from "@/components/dashboard/LiveTrainStatus";
import { PnrCardSkeleton } from "@/components/dashboard/skeletons";
import {
  fetchStatsPro,
  fetchTrend,
  fetchActivity,
  fetchHealthAll,
} from "@/lib/api-pro";

// Dev-only TweaksPanel — gated via require() inside a static
// NODE_ENV check so webpack tree-shakes the component entirely out of
// the production bundle. Verified post-build: grep 'TweaksPanel' on
// build/static/js/*.js returns no matches under NODE_ENV=production.
const TweaksPanelDev = (function loadTweaksPanel() {
  if (process.env.NODE_ENV !== "production") {
    try {
      // eslint-disable-next-line global-require
      return require("@/components/dashboard/TweaksPanel").default;
    } catch (_e) {
      return null;
    }
  }
  return null;
})();

const Dashboard = ({ user, onLogout }) => {
  const [pnrs, setPnrs] = useState([]);
  const [filteredPnrs, setFilteredPnrs] = useState([]);
  const [stats, setStats] = useState({ total_pnrs: 0, confirmed_seats: 0, rac_seats: 0, waiting_seats: 0, cancelled_seats: 0 });
  const [loading, setLoading] = useState(true);
  const [addingPnr, setAddingPnr] = useState(false);
  const [newPnr, setNewPnr] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkPnrs, setBulkPnrs] = useState("");
  const [selectedPnr, setSelectedPnr] = useState(null);
  const [seatChartOpen, setSeatChartOpen] = useState(false);
  const [seatChart, setSeatChart] = useState(null);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // 'confirmed', 'rac', 'waiting', 'cancelled'
  const [loadingSeatChart, setLoadingSeatChart] = useState(false);
  const [syncingPnr, setSyncingPnr] = useState(null);
  const [refreshingPnr, setRefreshingPnr] = useState(null);
  const [editPassengersOpen, setEditPassengersOpen] = useState(false);
  const [editingPnr, setEditingPnr] = useState(null);
  const [editedPassengers, setEditedPassengers] = useState([]);
  const [savingPassengers, setSavingPassengers] = useState(false);
  const [liveStatusOpen, setLiveStatusOpen] = useState(false);
  const [liveStatusPnrId, setLiveStatusPnrId] = useState(null);
  const [walletEnabled, setWalletEnabled] = useState(false);
  const [appleWalletEnabled, setAppleWalletEnabled] = useState(false);
  const [addingToWallet, setAddingToWallet] = useState(null);
  const [regeneratingWallets, setRegeneratingWallets] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [deletedPnrs, setDeletedPnrs] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [restoringPnr, setRestoringPnr] = useState(null);

  // Pro Dashboard state — KPI sparklines/deltas, trend chart, activity feed,
  // composed health snapshot, FilterRail active id, More-filters dialog.
  const [statsPro, setStatsPro] = useState(null);
  const [trend, setTrend] = useState([]);
  const [activity, setActivity] = useState([]);
  const [healthAll, setHealthAll] = useState(null);
  const [healthUpdatedAt, setHealthUpdatedAt] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Sidebar view — URL is the SINGLE source of truth. activeView is
  // derived from ?view= each render via useMemo. Avoids the
  // useState↔URL race that previously snapped the view back to
  // mission-control on every click (handleViewChange wrote to both,
  // and the post-write useEffect saw stale searchParams).
  const VALID_VIEWS = useMemo(() => new Set([
    "mission-control", "journeys", "live", "calendar", "wallet", "trash",
  ]), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = useMemo(() => {
    const v = searchParams.get("view");
    return v && VALID_VIEWS.has(v) ? v : "mission-control";
  }, [searchParams, VALID_VIEWS]);

  const handleViewChange = useCallback((id) => {
    if (!VALID_VIEWS.has(id)) return;
    const next = new URLSearchParams(searchParams);
    if (id === "mission-control") next.delete("view");
    else next.set("view", id);
    setSearchParams(next, { replace: true });
  }, [VALID_VIEWS, searchParams, setSearchParams]);

  // Keep the legacy showTrash flag mirrored to activeView so the
  // existing trash drawer + inline fetch handlers still apply. Driven
  // by the URL-derived activeView, so it stays consistent with the
  // view on refresh / back-button.
  useEffect(() => {
    setShowTrash(activeView === "trash");
  }, [activeView]);

  // Dev-only TweaksPanel state (always declared so hook order stays
  // stable; only the rendered panel is gated). In prod these stay at
  // defaults since the toggle UI is tree-shaken out.
  const [density, setDensity] = useState("default");
  const [showLiveOps, setShowLiveOps] = useState(true);
  const [showActivityFeed, setShowActivityFeed] = useState(true);

  const fetchPnrs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pnr?user_id=${user.id}`);
      setPnrs(response.data);
    } catch (e) {
      console.error("Failed to fetch PNRs:", e);
      toast.error("Failed to load PNRs");
    }
  }, [user.id]);

  const fetchDeletedPnrs = useCallback(async () => {
    setLoadingTrash(true);
    try {
      const response = await axios.get(`${API}/pnr/trash?user_id=${user.id}`);
      setDeletedPnrs(response.data);
    } catch (e) {
      console.error("Failed to fetch deleted PNRs:", e);
    } finally {
      setLoadingTrash(false);
    }
  }, [user.id]);

  // Lazy-load the deleted_pnrs collection whenever the user switches to the
  // Trash view (sidebar item or URL ?view=trash). Mirrors the inline fetch
  // that the legacy toggle-trash-btn does, but works from the sidebar too.
  useEffect(() => {
    if (activeView === "trash") {
      fetchDeletedPnrs();
    }
  }, [activeView, fetchDeletedPnrs]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats?user_id=${user.id}`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, [user.id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPnrs(), fetchStats()]);
      setLoading(false);
      setLastRefreshedAt(new Date());
    };
    loadData();

    // Check if Google Wallet is enabled
    const checkWalletStatus = async () => {
      try {
        const response = await axios.get(`${API}/wallet/status`);
        setWalletEnabled(response.data.enabled);
      } catch (e) {
        console.log("Google Wallet not available");
        setWalletEnabled(false);
      }
    };
    checkWalletStatus();

    // Check if Apple Wallet is enabled
    const checkAppleWalletStatus = async () => {
      try {
        const response = await axios.get(`${API}/apple-wallet/status`);
        setAppleWalletEnabled(response.data.enabled);
      } catch (e) {
        console.log("Apple Wallet not available");
        setAppleWalletEnabled(false);
      }
    };
    checkAppleWalletStatus();
  }, [fetchPnrs, fetchStats]);

  // Pro Dashboard data fetchers — all routed through lib/api-pro.js so a
  // single MOCK_PRO flag flip switches between Phase 1 mock data and the
  // real Phase 2 endpoints. Activity + health poll on a 30s cadence to
  // match the patterns already established by the legacy ApiHealthCard.
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;

    const loadStats = async () => {
      try {
        const res = await fetchStatsPro(user.id);
        if (!cancelled) setStatsPro(res.data);
      } catch (e) {
        if (!cancelled) console.error("statsPro fetch failed", e);
      }
    };
    const loadTrend = async () => {
      try {
        const res = await fetchTrend(user.id, 7);
        if (!cancelled) setTrend(res.data);
      } catch (e) {
        if (!cancelled) console.error("trend fetch failed", e);
      }
    };
    const loadActivity = async () => {
      try {
        const res = await fetchActivity(user.id, 20);
        if (!cancelled) setActivity(res.data);
      } catch (e) {
        if (!cancelled) console.error("activity fetch failed", e);
      }
    };
    const loadHealth = async () => {
      try {
        const res = await fetchHealthAll(user.id);
        if (cancelled) return;
        setHealthAll(res.data);
        setHealthUpdatedAt(new Date());
      } catch (e) {
        if (!cancelled) console.error("health fetch failed", e);
      }
    };

    loadStats();
    loadTrend();
    loadActivity();
    loadHealth();

    const activityId = setInterval(loadActivity, 30_000);
    const healthId = setInterval(loadHealth, 30_000);
    const statsId = setInterval(loadStats, 5 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(activityId);
      clearInterval(healthId);
      clearInterval(statsId);
    };
  }, [user?.id]);

  const handleAddToWallet = async (pnr) => {
    setAddingToWallet(pnr.id);
    try {
      // If PNR already has wallet URL, use it directly
      let walletUrl = pnr.google_wallet_url;
      
      if (!walletUrl) {
        // Generate wallet URL if not already present
        const response = await axios.get(`${API}/wallet/pass/${pnr.id}?user_id=${user.id}`);
        walletUrl = response.data.wallet_url;
        
        // Update local PNR data with the new wallet URL
        setPnrs(pnrs.map(p => p.id === pnr.id ? {...p, google_wallet_url: walletUrl} : p));
      }
      
      // Open Google Wallet directly in new tab
      window.open(walletUrl, '_blank');
      toast.success("Opening Google Wallet...");
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to generate wallet pass";
      toast.error(errorMsg);
    } finally {
      setAddingToWallet(null);
    }
  };

  const handleAddToAppleWallet = async (pnr) => {
    setAddingToWallet(pnr.id);
    try {
      // Download the .pkpass file
      const response = await axios.get(
        `${API}/apple-wallet/pass/${pnr.id}?user_id=${user.id}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.apple.pkpass' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `railsync-${pnr.pnr_number}.pkpass`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Apple Wallet pass downloaded! Open to add to Wallet.");
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to generate Apple Wallet pass";
      toast.error(errorMsg);
    } finally {
      setAddingToWallet(null);
    }
  };

  const handleRegenerateAllWallets = async () => {
    if (!walletEnabled) {
      toast.error("Google Wallet is not enabled");
      return;
    }
    
    setRegeneratingWallets(true);
    try {
      const response = await axios.post(`${API}/wallet/regenerate-all?user_id=${user.id}`);
      toast.success(response.data.message);
      // Refresh PNRs to get updated wallet URLs
      fetchPnrs();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to regenerate wallet passes";
      toast.error(errorMsg);
    } finally {
      setRegeneratingWallets(false);
    }
  };

  const handleShareOnTelegram = (pnr) => {
    // Format passenger statuses
    const passengerInfo = pnr.passengers.map(p => {
      let status = p.current_status || "Unknown";
      let seatInfo = p.coach && p.seat_number ? ` (${p.coach}-${p.seat_number})` : "";
      return `• ${p.name}: ${status}${seatInfo}`;
    }).join("\n");

    // Build share message
    const message = `🚂 *PNR: ${pnr.pnr_number}*

Train: ${pnr.train_number} - ${pnr.train_name}
📅 Date: ${pnr.journey_date}
📍 Route: ${pnr.boarding_station_name || pnr.boarding_station} → ${pnr.destination_station_name || pnr.destination_station}
🕐 Time: ${pnr.departure_time} - ${pnr.arrival_time}

👥 Passengers:
${passengerInfo}

📊 Chart: ${pnr.chart_status}

_Shared via RailSync_`;

    // Encode for Telegram URL
    const encodedMessage = encodeURIComponent(message);
    const telegramUrl = `https://t.me/share/url?url=&text=${encodedMessage}`;
    
    window.open(telegramUrl, '_blank');
    toast.success("Opening Telegram share...");
  };

  const handleRestorePnr = async (pnrId) => {
    setRestoringPnr(pnrId);
    try {
      await axios.post(`${API}/pnr/trash/${pnrId}/restore?user_id=${user.id}`);
      toast.success("PNR restored successfully!");
      fetchDeletedPnrs();
      fetchPnrs();
      fetchStats();
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to restore PNR";
      toast.error(message);
    } finally {
      setRestoringPnr(null);
    }
  };

  const handlePermanentDelete = async (pnrId) => {
    if (!window.confirm("Are you sure you want to permanently delete this PNR? This cannot be undone.")) {
      return;
    }
    try {
      await axios.delete(`${API}/pnr/trash/${pnrId}/permanent?user_id=${user.id}`);
      toast.success("PNR permanently deleted");
      fetchDeletedPnrs();
    } catch (e) {
      toast.error("Failed to delete PNR");
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm("Are you sure you want to permanently delete ALL deleted PNRs? This cannot be undone.")) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/pnr/trash/empty?user_id=${user.id}`);
      toast.success(response.data.message);
      setDeletedPnrs([]);
    } catch (e) {
      toast.error("Failed to empty trash");
    }
  };

  const handleAddPnr = async (e) => {
    e.preventDefault();
    
    if (bulkMode) {
      // Bulk add mode
      const pnrList = bulkPnrs
        .split(/[\n,]+/)
        .map(p => p.trim())
        .filter(p => p.length > 0);
      
      if (pnrList.length === 0) {
        toast.error("Please enter at least one PNR number");
        return;
      }
      
      setAddingPnr(true);
      try {
        const response = await axios.post(`${API}/pnr/bulk?user_id=${user.id}`, {
          pnr_numbers: pnrList
        });
        
        const { success, failed } = response.data;
        
        if (success.length > 0) {
          setPnrs([...success, ...pnrs]);
          toast.success(`Added ${success.length} PNR(s) successfully!`);
          
          // Auto-open Google Wallet for the first PNR with wallet URL
          const firstWithWallet = success.find(p => p.google_wallet_url);
          if (firstWithWallet && walletEnabled) {
            window.open(firstWithWallet.google_wallet_url, '_blank');
            toast.success("Opening Google Wallet...");
            if (success.length > 1) {
              toast.info(`Tip: Click wallet icon to add other ${success.length - 1} PNR(s) to Google Wallet`);
            }
          }
        }
        
        if (failed.length > 0) {
          failed.forEach(f => {
            toast.error(`${f.pnr_number}: ${f.error}`);
          });
        }
        
        setBulkPnrs("");
        fetchStats();
      } catch (e) {
        toast.error("Failed to add PNRs");
      } finally {
        setAddingPnr(false);
      }
    } else {
      // Single PNR mode
      if (!newPnr.trim() || newPnr.length !== 10) {
        toast.error("Please enter a valid 10-digit PNR number");
        return;
      }

      setAddingPnr(true);
      try {
        const response = await axios.post(`${API}/pnr?user_id=${user.id}`, {
          pnr_number: newPnr.trim()
        });
        setPnrs([response.data, ...pnrs]);
        setNewPnr("");
        fetchStats();
        
        // Show different message based on data source
        if (response.data.is_real_data === false) {
          toast.warning("PNR added with demo data. RapidAPI subscription may need renewal.");
        } else {
          toast.success("PNR added with real IRCTC data!");
        }
        
        // Show calendar sync status
        if (response.data.calendar_synced) {
          toast.success("Auto-synced to Google Calendar (RailSync Train Tickets)");
        }
        
        // Auto-open Google Wallet directly if wallet URL was generated
        if (response.data.google_wallet_url && walletEnabled) {
          window.open(response.data.google_wallet_url, '_blank');
          toast.success("Opening Google Wallet...");
        }
      } catch (e) {
        const message = e.response?.data?.detail || "Failed to add PNR";
        toast.error(message);
      } finally {
        setAddingPnr(false);
      }
    }
  };

  const handleDeletePnr = async (pnrId) => {
    try {
      await axios.delete(`${API}/pnr/${pnrId}?user_id=${user.id}`);
      setPnrs(pnrs.filter(p => p.id !== pnrId));
      fetchStats();
      toast.success("PNR moved to trash");
      // Refresh trash if visible
      if (showTrash) fetchDeletedPnrs();
    } catch (e) {
      toast.error("Failed to delete PNR");
    }
  };

  const handleRefreshPnr = async (pnrId) => {
    setRefreshingPnr(pnrId);
    try {
      const response = await axios.put(`${API}/pnr/${pnrId}/refresh?user_id=${user.id}`);
      
      // Check if API returned error (still using old data)
      if (response.data.refresh_error) {
        toast.warning(response.data.refresh_error);
      } else if (response.data.is_real_data === false) {
        toast.warning("PNR API is temporarily unavailable. Using cached data.");
      } else {
        toast.success("PNR status updated");
      }
      
      setPnrs(pnrs.map(p => p.id === pnrId ? response.data : p));
      fetchStats();
    } catch (e) {
      toast.error("Failed to refresh PNR");
    } finally {
      setRefreshingPnr(null);
    }
  };

  const [refreshingAllMock, setRefreshingAllMock] = useState(false);
  
  const handleRefreshAllMockData = async () => {
    setRefreshingAllMock(true);
    try {
      const response = await axios.put(`${API}/pnr/refresh-all?user_id=${user.id}`);
      const { refreshed, failed, skipped, api_available } = response.data;
      
      if (!api_available && failed.length > 0) {
        toast.error(`RapidAPI is still unavailable. ${failed.length} PNRs could not be refreshed.`);
      } else if (refreshed.length > 0) {
        toast.success(`Successfully refreshed ${refreshed.length} PNRs with real data!`);
        fetchPnrs();
        fetchStats();
      } else if (skipped.length > 0 && failed.length === 0) {
        toast.info("All PNRs already have real data.");
      } else {
        toast.warning("No PNRs were refreshed. API may still be unavailable.");
      }
    } catch (e) {
      toast.error("Failed to refresh PNRs");
    } finally {
      setRefreshingAllMock(false);
    }
  };

  const handleSyncCalendar = async (pnrId) => {
    setSyncingPnr(pnrId);
    try {
      await axios.post(`${API}/calendar/sync?user_id=${user.id}`, { pnr_id: pnrId });
      toast.success("Synced to Google Calendar!");
      fetchPnrs();
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to sync to calendar";
      toast.error(message);
    } finally {
      setSyncingPnr(null);
    }
  };

  const [syncingAllCalendar, setSyncingAllCalendar] = useState(false);
  const [refreshingAllStatus, setRefreshingAllStatus] = useState(false);

  const handleSyncAllCalendar = async () => {
    setSyncingAllCalendar(true);
    try {
      const response = await axios.post(`${API}/calendar/sync-all?user_id=${user.id}`);
      const { synced, failed, total_pnrs } = response.data;
      
      if (synced > 0) {
        toast.success(`Synced ${synced}/${total_pnrs} PNRs to Google Calendar!`);
        fetchPnrs();
      } else if (failed > 0) {
        toast.error(`Failed to sync ${failed} PNRs. Check if Google account is connected.`);
      } else {
        toast.info("No PNRs to sync.");
      }
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to sync all to calendar";
      toast.error(message);
    } finally {
      setSyncingAllCalendar(false);
    }
  };

  const handleRefreshAllStatus = async () => {
    setRefreshingAllStatus(true);
    try {
      const response = await axios.post(`${API}/pnr/refresh-all-status?user_id=${user.id}`);
      const { refreshed, api_errors, total_pnrs } = response.data;
      
      if (refreshed > 0) {
        toast.success(`Refreshed ${refreshed}/${total_pnrs} PNRs from IRCTC API!`);
        fetchPnrs();
        fetchStats();
      } else if (api_errors > 0) {
        toast.warning(`API unavailable for ${api_errors} PNRs. Try again later.`);
      } else {
        toast.info("No PNRs to refresh.");
      }
    } catch (e) {
      toast.error("Failed to refresh PNR status");
    } finally {
      setRefreshingAllStatus(false);
    }
  };

  const handleEditPassengers = (pnr) => {
    setEditingPnr(pnr);
    setEditedPassengers(pnr.passengers.map(p => ({ 
      name: p.name, 
      age: p.age || 0, 
      gender: p.gender || 'M' 
    })));
    setEditPassengersOpen(true);
  };

  const handleSavePassengers = async () => {
    if (!editingPnr) return;
    
    setSavingPassengers(true);
    try {
      const response = await axios.put(
        `${API}/pnr/${editingPnr.id}/passengers?user_id=${user.id}`,
        { passengers: editedPassengers }
      );
      setPnrs(pnrs.map(p => p.id === editingPnr.id ? response.data : p));
      setEditPassengersOpen(false);
      toast.success("Passenger names updated!");
    } catch (e) {
      toast.error("Failed to update passengers");
    } finally {
      setSavingPassengers(false);
    }
  };

  const updatePassengerName = (index, name) => {
    const updated = [...editedPassengers];
    updated[index] = { ...updated[index], name };
    setEditedPassengers(updated);
  };

  const handleViewSeatChart = async (pnr) => {
    setSelectedPnr(pnr);
    setSeatChartOpen(true);
    setLoadingSeatChart(true);
    
    try {
      const coach = pnr.passengers[0]?.coach || "S1";
      const response = await axios.get(`${API}/seat-chart/${pnr.train_number}/${coach}`);
      setSeatChart(response.data);
    } catch (e) {
      toast.error("Failed to load seat chart");
    } finally {
      setLoadingSeatChart(false);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toUpperCase() || '';
    if (s.includes('CNF') || s.includes('CONFIRMED')) return "status-confirmed";
    if (s.includes('RAC')) return "status-rac";
    if (s.includes('WL') || s.includes('WAITING') || s.includes('RLWL') || s.includes('GNWL') || s.includes('PQWL')) return "status-waiting";
    if (s.includes('CAN') || s.includes('CANCELLED')) return "bg-red-500/20 text-red-400 border-red-500/30";
    return "bg-zinc-700 text-zinc-300";
  };

  const getStatusIcon = (status) => {
    const s = status?.toUpperCase() || '';
    if (s.includes('CNF') || s.includes('CONFIRMED')) return <CheckCircle className="w-4 h-4" />;
    if (s.includes('RAC')) return <MinusCircle className="w-4 h-4" />;
    if (s.includes('WL') || s.includes('WAITING') || s.includes('RLWL') || s.includes('GNWL') || s.includes('PQWL')) return <XCircle className="w-4 h-4" />;
    if (s.includes('CAN') || s.includes('CANCELLED')) return <AlertCircle className="w-4 h-4 text-red-400" />;
    return <AlertCircle className="w-4 h-4" />;
  };

  // Get passengers filtered by status
  const getFilteredPassengers = (filterType) => {
    const filtered = [];
    pnrs.forEach(pnr => {
      pnr.passengers?.forEach(p => {
        const status = (p.current_status || '').toUpperCase();
        let matches = false;
        
        switch(filterType) {
          case 'confirmed':
            matches = status.includes('CNF') || status.includes('CONFIRMED');
            break;
          case 'rac':
            matches = status.includes('RAC');
            break;
          case 'waiting':
            matches = status.includes('WL') || status.includes('WAITING') || status.includes('RLWL') || status.includes('GNWL') || status.includes('PQWL') || status.includes('RSWL') || status.includes('TQWL');
            break;
          case 'cancelled':
            matches = status.includes('CAN') || status.includes('CANCELLED');
            break;
          default:
            break;
        }
        
        if (matches) {
          filtered.push({
            ...p,
            pnr_number: pnr.pnr_number,
            train_name: pnr.train_name,
            train_number: pnr.train_number,
            journey_date: pnr.journey_date,
            boarding_station: pnr.boarding_station,
            destination_station: pnr.destination_station
          });
        }
      });
    });
    return filtered;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // ─── Pro Dashboard derived values ──────────────────────────────────────
  // statsForKpi merges the legacy /api/stats response with the extended
  // pro fields (deltas + spark_*). If statsPro hasn't loaded yet, we fall
  // back to plain stats — KpiStrip handles missing sparks/deltas gracefully.
  const statsForKpi = useMemo(() => ({ ...stats, ...(statsPro || {}) }), [stats, statsPro]);

  // Featured live PNR for LiveOpsPanel. In Phase 1 we look at pnr.live
  // (mock-data shape); Phase 2 backend will compute this server-side so
  // any PNR within ±6h of departure carries live=true.
  const featuredLivePnr = useMemo(
    () => pnrs.find((p) => p && p.live),
    [pnrs]
  );

  // Next upcoming PNR for NextDeparture — earliest journey_date that's
  // still in the future (or within 12h past departure; the card handles
  // the departed-but-not-arrived case itself).
  const nextDeparturePnr = useMemo(() => {
    const now = Date.now();
    return pnrs
      .filter((p) => {
        const ts = Date.parse(`${p.journey_date}T${p.departure_time || "00:00"}:00`);
        return !Number.isNaN(ts) && ts > now - 12 * 3600 * 1000;
      })
      .sort((a, b) => {
        const ta = Date.parse(`${a.journey_date}T${a.departure_time || "00:00"}:00`);
        const tb = Date.parse(`${b.journey_date}T${b.departure_time || "00:00"}:00`);
        return ta - tb;
      })[0];
  }, [pnrs]);

  // FilterRail counts.
  const filterCounts = useMemo(() => {
    const upcoming = pnrs.filter((p) => {
      const ts = Date.parse(`${p.journey_date}T${p.departure_time || "00:00"}:00`);
      return !Number.isNaN(ts) && ts > Date.now();
    }).length;
    const liveCount = pnrs.filter((p) => p && p.live).length;
    return {
      total: pnrs.length,
      upcoming,
      live: liveCount,
      confirmed: stats.confirmed_seats || 0,
      rac: stats.rac_seats || 0,
      waiting: stats.waiting_seats || 0,
    };
  }, [pnrs, stats]);

  // FilterRail's active filter narrows the journey list. statusFilter
  // (legacy) is still respected when the FilterRail is on 'all' so the
  // existing KpiStrip click-to-filter flow keeps working.
  const filteredByActive = useMemo(() => {
    if (activeFilter === "all" && !statusFilter) return filteredPnrs;
    let list = filteredPnrs;
    if (activeFilter === "upcoming") {
      list = list.filter((p) => {
        const ts = Date.parse(`${p.journey_date}T${p.departure_time || "00:00"}:00`);
        return !Number.isNaN(ts) && ts > Date.now();
      });
    } else if (activeFilter === "live") {
      list = list.filter((p) => p && p.live);
    } else if (["confirmed", "rac", "waiting"].includes(activeFilter)) {
      list = list.filter((p) =>
        (p.passengers || []).some((pax) => {
          const s = (pax.current_status || "").toUpperCase();
          if (activeFilter === "confirmed") return s.includes("CNF") || s.includes("CONFIRMED");
          if (activeFilter === "rac") return s.includes("RAC");
          if (activeFilter === "waiting") return s.includes("WL") || s.includes("WAITING");
          return true;
        })
      );
    }
    return list;
  }, [filteredPnrs, activeFilter, statusFilter]);

  // ─── Sidebar view filter ───────────────────────────────────────────
  // Layered ON TOP of filteredByActive (which already accounts for
  // FilterRail + KpiStrip selections). The two compose, so e.g. on the
  // Calendar view a "Confirmed" pill click further narrows the list.
  const viewFilteredPnrs = useMemo(() => {
    if (activeView === "mission-control" || activeView === "journeys") {
      return filteredByActive;
    }
    if (activeView === "live") {
      return filteredByActive.filter((p) => p && p.live);
    }
    if (activeView === "calendar") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return filteredByActive
        .filter((p) => {
          if (!p?.journey_date) return false;
          const d = new Date(p.journey_date);
          return !Number.isNaN(d.getTime()) && d >= today;
        })
        .sort((a, b) => new Date(a.journey_date) - new Date(b.journey_date));
    }
    if (activeView === "wallet") {
      return filteredByActive.filter(
        (p) => p && (p.google_wallet_url || p.apple_wallet_url)
      );
    }
    // Trash view doesn't render the PNR list at all — the deleted_pnrs
    // collection has its own renderer below. Return [] so the journeys
    // section short-circuits to "No journeys" rather than the active list.
    if (activeView === "trash") return [];
    return filteredByActive;
  }, [activeView, filteredByActive]);

  // Boolean gates per section, keyed off the sidebar view.
  const sectionVisible = useMemo(() => ({
    greeting:        activeView !== "trash",
    kpiStrip:        true,
    mockBanner:      activeView !== "trash",
    liveOpsPanel:    activeView === "mission-control" || activeView === "live",
    quickAdd:        activeView !== "trash",
    trendCard:       activeView === "mission-control",
    journeysSection: true,
    activityFeed:    activeView === "mission-control",
    apiHealth:       activeView === "mission-control",
    walletCard:      activeView === "mission-control" || activeView === "wallet",
    nextDeparture:   activeView === "mission-control" || activeView === "calendar",
    trashDrawer:     activeView === "trash",
  }), [activeView]);

  // Contextual subtitle for the greeting block.
  const greetingSubtitle = useMemo(() => {
    if (loading) return "Loading your journeys…";
    if (pnrs.length === 0) return "No journeys tracked yet — add a PNR to begin.";
    const bits = [];
    if (filterCounts.live > 0) {
      bits.push(`${filterCounts.live} train${filterCounts.live > 1 ? "s" : ""} in transit`);
    }
    if (filterCounts.waiting > 0) {
      bits.push(`${filterCounts.waiting} waitlist seat${filterCounts.waiting > 1 ? "s" : ""} moving`);
    }
    if (filterCounts.upcoming > 0) {
      bits.push(`${filterCounts.upcoming} upcoming`);
    }
    return bits.length ? bits.join(" · ") : "All journeys are settled.";
  }, [loading, pnrs.length, filterCounts]);

  // Sidebar nav counts.
  const sidebarCounts = useMemo(() => ({
    journeys: pnrs.length,
    live: filterCounts.live,
    trash: deletedPnrs.length,
  }), [pnrs.length, filterCounts.live, deletedPnrs.length]);

  // Sidebar health summary — derived from /api/health/all (or its mock).
  const sidebarHealth = useMemo(() => {
    if (!healthAll) return undefined;
    const services = {
      irctc: healthAll.irctc?.status || "operational",
      ntes: healthAll.ntes?.status || "operational",
      calendar: healthAll.calendar?.status || "operational",
      wallet: healthAll.wallet?.status || "operational",
    };
    const latencies = Object.values({
      irctc: healthAll.irctc?.latency,
      ntes: healthAll.ntes?.latency,
      calendar: healthAll.calendar?.latency,
      wallet: healthAll.wallet?.latency,
    }).filter((n) => typeof n === "number");
    const avg = latencies.length
      ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
      : null;
    const allOk = Object.values(services).every((s) => s === "operational");
    return { allOk, latencyMs: avg, services };
  }, [healthAll]);

  const onTrackPnrCta = () => {
    const el = document.querySelector('[data-testid="pnr-input"]') ||
      document.querySelector('[data-testid="bulk-pnr-input"]');
    if (el && typeof el.focus === "function") el.focus();
  };

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="dashboard-root">
      <div className="flex">
        {/* Sidebar (sticky, 244px). Workspace items dispatch view changes
            via handleViewChange; the user pod is wrapped in a real
            DropdownMenu (Settings + Sign Out items) via the userMenuSlot
            render prop so logout-btn lives where users expect it. */}
        <Sidebar
          user={user}
          counts={sidebarCounts}
          health={sidebarHealth}
          activeView={activeView}
          onViewChange={handleViewChange}
          onCommandOpen={() => toast.info("Command palette coming soon")}
          userMenuSlot={({ children, className, testId }) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={className} data-testid={testId}>
                  {children}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                <DropdownMenuItem className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50">
                  <span className="text-sm text-zinc-500">{user?.email}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <Link to="/settings">
                  <DropdownMenuItem className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50 cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                    {user?.totp_enabled && (
                      <Badge variant="outline" className="ml-auto text-xs bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        2FA
                      </Badge>
                    )}
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuItem
                  onClick={onLogout}
                  className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        />

        {/* Main column */}
        <div className="flex-1 min-w-0 flex flex-col">
          <TopBar
            onAddPnr={onTrackPnrCta}
            lastRefreshedAt={lastRefreshedAt}
            onBellClick={() => toast.info(activity.length ? `${activity.length} recent activities` : "No new notifications")}
            hasNotifications={filterCounts.live > 0}
          />

          <main className="flex-1 w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-7 relative z-10">
            {sectionVisible.greeting && (
              <GreetingBlock
                loading={loading}
                user={user}
                activeJourneys={filterCounts.live}
                subtitle={greetingSubtitle}
                onRefreshAll={handleRefreshAllStatus}
                onSyncCalendar={handleSyncAllCalendar}
                isRefreshing={refreshingAllStatus}
                isSyncingCal={syncingAllCalendar}
              />
            )}

            {sectionVisible.kpiStrip && (
              <div className="mt-5">
                <KpiStrip
                  stats={statsForKpi}
                  loading={loading}
                  onFilterClick={(filter) => {
                    setStatusFilter(filter);
                    setStatusFilterOpen(true);
                  }}
                />
              </div>
            )}

            {sectionVisible.mockBanner && (
              <div className="mt-5">
                <MockDataBanner
                  mockPnrCount={pnrs.filter(p => p.is_real_data === false).length}
                  onRefreshAll={handleRefreshAllMockData}
                  isRefreshing={refreshingAllMock}
                />
              </div>
            )}

            <div className="grid grid-cols-12 gap-5 mt-5">
              {/* Left column — col-span-8 */}
              <div className="col-span-12 lg:col-span-8 space-y-5">
                {sectionVisible.liveOpsPanel && featuredLivePnr && showLiveOps && (
                  <LiveOpsPanel
                    data={featuredLivePnr.live_data || featuredLivePnr}
                    onRefresh={() => handleRefreshPnr(featuredLivePnr.id)}
                    onShare={() => handleShareOnTelegram(featuredLivePnr)}
                    isRefreshing={refreshingPnr === featuredLivePnr.id}
                  />
                )}

                {sectionVisible.quickAdd && (
                  <QuickAddPnr
                    newPnr={newPnr}
                    setNewPnr={setNewPnr}
                    bulkMode={bulkMode}
                    setBulkMode={setBulkMode}
                    bulkPnrs={bulkPnrs}
                    setBulkPnrs={setBulkPnrs}
                    onSubmit={handleAddPnr}
                    isLoading={addingPnr}
                  />
                )}

                {sectionVisible.trendCard && (
                  <TrendCard data={trend} loading={trend.length === 0} />
                )}

                <section className={sectionVisible.journeysSection ? "space-y-3" : "hidden"}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="text-[15px] font-semibold text-zinc-100 font-heading">
                      {activeView === "trash" ? "Trash"
                        : activeView === "live" ? "Live journeys"
                        : activeView === "calendar" ? "Upcoming journeys"
                        : activeView === "wallet" ? "Wallet passes"
                        : "Your journeys"}
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewChange(activeView === "trash" ? "mission-control" : "trash")}
                      className={`text-zinc-400 hover:text-rose-400 ${activeView === "trash" ? 'bg-rose-900/30 text-rose-400' : ''}`}
                      data-testid="toggle-trash-btn"
                      title="View deleted PNRs"
                    >
                      <Trash className="w-4 h-4 mr-2" />
                      Trash {deletedPnrs.length > 0 && `(${deletedPnrs.length})`}
                    </Button>
                  </div>

                  {/* FilterRail doesn't apply to deleted PNRs — hide it
                      on the trash view (drawer below has its own
                      Empty-trash control). */}
                  {activeView !== "trash" && (
                    <FilterRail
                      active={activeFilter}
                      counts={filterCounts}
                      onFilterChange={setActiveFilter}
                      onMoreFilters={() => setMoreFiltersOpen(true)}
                      onExport={() => toast.info("Export coming soon")}
                    />
                  )}

                  {/* Trash drawer (kept for testid + behavior) */}
                  {showTrash && (
                    <Card className="bg-rose-950/20 border-rose-900/50">
                      <CardHeader className="flex flex-row items-center justify-between py-3">
                        <CardTitle className="text-rose-400 text-base flex items-center gap-2">
                          <Trash className="w-4 h-4" />
                          Deleted PNRs ({deletedPnrs.length})
                        </CardTitle>
                        {deletedPnrs.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleEmptyTrash}
                            className="text-rose-400 hover:text-rose-300 hover:bg-rose-900/30"
                          >
                            Empty Trash
                          </Button>
                        )}
                      </CardHeader>
                      <CardContent className="pt-0">
                        {loadingTrash ? (
                          <div className="space-y-3">
                            {[0, 1, 2].map((i) => (
                              <PnrCardSkeleton key={i} index={i} />
                            ))}
                          </div>
                        ) : deletedPnrs.length === 0 ? (
                          <div className="text-center py-4 text-zinc-500">
                            <Archive className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            Trash is empty
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {deletedPnrs.map((pnr) => (
                              <div
                                key={pnr.original_id}
                                className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-3">
                                    <span className="font-mono text-zinc-300">{pnr.pnr_number}</span>
                                    <span className="text-zinc-500 text-sm">{pnr.train_name}</span>
                                  </div>
                                  <div className="text-xs text-zinc-600 mt-1">
                                    Deleted: {new Date(pnr.deleted_at).toLocaleString('en-IN')}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRestorePnr(pnr.original_id)}
                                    disabled={restoringPnr === pnr.original_id}
                                    className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30"
                                    title="Restore PNR"
                                  >
                                    <RotateCcw className={`w-4 h-4 ${restoringPnr === pnr.original_id ? 'animate-spin' : ''}`} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePermanentDelete(pnr.original_id)}
                                    className="text-rose-400 hover:text-rose-300 hover:bg-rose-900/30"
                                    title="Delete permanently"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* PNR list — hidden on the Trash view since the
                      drawer above already lists deleted PNRs and
                      viewFilteredPnrs is intentionally empty there. */}
                  {activeView === "trash" ? null : loading ? (
                    <div className="space-y-3">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <PnrCardSkeleton key={i} index={i} />
                      ))}
                    </div>
                  ) : pnrs.length === 0 ? (
                    <Card className="bg-zinc-900/40 border-zinc-800/60 rounded-xl">
                      <CardContent className="p-12 text-center">
                        <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                          <Train className="w-8 h-8 text-zinc-500" />
                        </div>
                        <h3 className="text-lg font-medium text-zinc-300 mb-2">No PNRs added yet</h3>
                        <p className="text-zinc-500 mb-4">Add your first PNR number to start tracking</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {/* Hidden PNRFilters keeps the filter pipeline running
                          (URL → filteredPnrs) and resolves the legacy
                          filter-* testids without the dialog needing to be
                          open. The visible UI lives inside the More-filters
                          Dialog below. */}
                      <div className="hidden">
                        <PNRFilters pnrs={pnrs} onFilteredChange={setFilteredPnrs} />
                      </div>

                      {viewFilteredPnrs.length === 0 ? (
                        <Card className="bg-zinc-900/40 border-zinc-800/60 rounded-xl">
                          <CardContent className="p-8 text-center text-zinc-500">
                            No journeys match the current filter.
                          </CardContent>
                        </Card>
                      ) : (
                        viewFilteredPnrs.map((pnr, index) => (
                          <PnrCard
                            key={pnr.id}
                            pnr={pnr}
                            index={index}
                            onRefresh={handleRefreshPnr}
                            onDelete={handleDeletePnr}
                            onSyncCalendar={handleSyncCalendar}
                            onAddToWallet={handleAddToWallet}
                            onAddToAppleWallet={handleAddToAppleWallet}
                            onEditPassengers={handleEditPassengers}
                            onViewTrainStatus={(p) => { setLiveStatusPnrId(p.id); setLiveStatusOpen(true); }}
                            onViewSeatChart={handleViewSeatChart}
                            onShareTelegram={handleShareOnTelegram}
                            isRefreshing={refreshingPnr === pnr.id}
                            isSyncing={syncingPnr === pnr.id}
                            isAddingWallet={addingToWallet === pnr.id}
                            walletEnabled={walletEnabled}
                            appleWalletEnabled={appleWalletEnabled}
                          />
                        ))
                      )}
                    </div>
                  )}
                </section>
              </div>

              {/* Right rail — col-span-4. Sections gated by sectionVisible
                  so e.g. the Wallet view's right rail shows ONLY the
                  WalletPassesCard, not ActivityFeed / Health / etc. */}
              <div className="col-span-12 lg:col-span-4 space-y-5">
                {sectionVisible.activityFeed && showActivityFeed && (
                  <ActivityFeed items={activity} loading={!activity.length && loading} />
                )}
                {sectionVisible.apiHealth && (
                  <ApiHealthCard
                    health={healthAll}
                    updatedAt={healthUpdatedAt}
                    loading={!healthAll}
                  />
                )}
                {sectionVisible.walletCard && (
                  <WalletPassesCard
                    loading={loading}
                    googleCount={pnrs.filter(p => p.google_wallet_url).length}
                    appleCount={pnrs.filter(p => p.apple_wallet_url).length}
                    googleEnabled={walletEnabled}
                    appleEnabled={appleWalletEnabled}
                    onRegenerate={walletEnabled ? handleRegenerateAllWallets : undefined}
                    isRegenerating={regeneratingWallets}
                  />
                )}
                {sectionVisible.nextDeparture && (
                  <NextDeparture pnr={nextDeparturePnr} loading={loading} />
                )}
              </div>
            </div>

            <DashboardFooter />

            {/* Dev-only TweaksPanel — tree-shaken from production bundle
                via the static require() guard at the top of this file.
                In prod TweaksPanelDev is null and this branch never
                evaluates the JSX. */}
            {TweaksPanelDev && (
              <TweaksPanelDev
                density={density}
                setDensity={setDensity}
                showLiveOps={showLiveOps}
                setShowLiveOps={setShowLiveOps}
                showActivityFeed={showActivityFeed}
                setShowActivityFeed={setShowActivityFeed}
              />
            )}

            {/* Hidden — keeps legacy filteredPnrs JSX mounted with the
                inline-card structure ZERO times so we never collide with
                the new PnrCard render but the old inline filter mapping
                stays in scope for setFilteredPnrs above. */}
            {false && pnrs.length === 0 && filteredPnrs.map((pnr, index) => (
                <Card 
                  key={pnr.id} 
                  className="bg-zinc-900/40 border-zinc-800/50 card-hover overflow-hidden animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`pnr-card-${pnr.pnr_number}`}
                >
                  <CardContent className="p-6">
                    {/* Header Row */}
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>
                            {pnr.train_name}
                          </h3>
                          <Badge variant="outline" className="bg-zinc-800/50 text-zinc-400 border-zinc-700">
                            {pnr.train_number}
                          </Badge>
                          {pnr.is_real_data === false && (
                            <Badge 
                              variant="outline" 
                              className="bg-amber-900/30 text-amber-400 border-amber-600/50 text-xs"
                              title="RapidAPI quota exceeded - showing demo data"
                            >
                              Demo Data
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400">
                          PNR: <span className="text-zinc-200 font-mono">{pnr.pnr_number}</span>
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {(() => {
                          // Show "Live Status" only when journey is within ±2
                          // days of today — backend determines actual active
                          // window, this is just a UI hint to reduce noise.
                          const j = pnr.journey_date;
                          if (!j) return null;
                          const today = new Date();
                          const journey = new Date(j);
                          if (Number.isNaN(journey.getTime())) return null;
                          const diffDays = Math.abs(
                            (journey.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
                          );
                          if (diffDays > 2) return null;
                          return (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setLiveStatusPnrId(pnr.id);
                                setLiveStatusOpen(true);
                              }}
                              className="text-zinc-400 hover:text-sky-400"
                              data-testid={`live-status-${pnr.pnr_number}`}
                              title="Live train status"
                            >
                              <Radio className="w-4 h-4" />
                            </Button>
                          );
                        })()}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPassengers(pnr)}
                          className="text-zinc-400 hover:text-emerald-400"
                          data-testid={`edit-passengers-${pnr.pnr_number}`}
                          title="Edit passenger names"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRefreshPnr(pnr.id)}
                          disabled={refreshingPnr === pnr.id}
                          className="text-zinc-400 hover:text-zinc-50"
                          data-testid={`refresh-pnr-${pnr.pnr_number}`}
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshingPnr === pnr.id ? 'spinner' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewSeatChart(pnr)}
                          className="text-zinc-400 hover:text-zinc-50"
                          data-testid={`seat-chart-${pnr.pnr_number}`}
                        >
                          <LayoutGrid className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSyncCalendar(pnr.id)}
                          disabled={syncingPnr === pnr.id}
                          className="text-zinc-400 hover:text-indigo-400"
                          data-testid={`sync-calendar-${pnr.pnr_number}`}
                          title="Sync to Google Calendar"
                        >
                          <Calendar className={`w-4 h-4 ${syncingPnr === pnr.id ? 'spinner' : ''}`} />
                        </Button>
                        {walletEnabled && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAddToWallet(pnr)}
                            disabled={addingToWallet === pnr.id}
                            className="text-zinc-400 hover:text-green-400"
                            data-testid={`add-wallet-${pnr.pnr_number}`}
                            title="Add to Google Wallet"
                          >
                            <Wallet className={`w-4 h-4 ${addingToWallet === pnr.id ? 'spinner' : ''}`} />
                          </Button>
                        )}
                        {appleWalletEnabled && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAddToAppleWallet(pnr)}
                            disabled={addingToWallet === pnr.id}
                            className="text-zinc-400 hover:text-blue-400"
                            data-testid={`add-apple-wallet-${pnr.pnr_number}`}
                            title="Add to Apple Wallet"
                          >
                            <svg className={`w-4 h-4 ${addingToWallet === pnr.id ? 'spinner' : ''}`} viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                            </svg>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleShareOnTelegram(pnr)}
                          className="text-zinc-400 hover:text-sky-400"
                          data-testid={`share-telegram-${pnr.pnr_number}`}
                          title="Share on Telegram"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePnr(pnr.id)}
                          className="text-zinc-400 hover:text-rose-400"
                          data-testid={`delete-pnr-${pnr.pnr_number}`}
                          title="Delete PNR"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Journey Details */}
                    <div className="grid sm:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <Clock className="w-4 h-4 text-zinc-400" />
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Date</p>
                          <p className="text-sm text-zinc-200">{formatDate(pnr.journey_date)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-zinc-400" />
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Route</p>
                          <p className="text-sm text-zinc-200">
                            {pnr.boarding_station} → {pnr.destination_station}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-zinc-400" />
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Class</p>
                          <p className="text-sm text-zinc-200">{pnr.journey_class}</p>
                        </div>
                      </div>
                    </div>

                    {/* Passengers */}
                    <div className="border-t border-zinc-800/50 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">Passengers</p>
                        <Badge 
                          variant="outline" 
                          className={`${pnr.chart_status === "CHART PREPARED" ? "status-confirmed" : "bg-zinc-800/50 text-zinc-400 border-zinc-700"}`}
                        >
                          {pnr.chart_status}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2">
                        {pnr.passengers.map((passenger, pIndex) => (
                          <div 
                            key={pIndex}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-zinc-800/30"
                            data-testid={`passenger-${pnr.pnr_number}-${pIndex}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-zinc-200">{passenger.name}</span>
                              <span className="text-xs text-zinc-500">
                                {passenger.age}/{passenger.gender}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              {passenger.coach && (
                                <span className="text-xs text-zinc-400 font-mono">
                                  {passenger.coach}/{passenger.seat_number}/{passenger.berth}
                                </span>
                              )}
                              <Badge 
                                variant="outline"
                                className={`flex items-center gap-1 ${getStatusColor(passenger.current_status)}`}
                                data-testid={`status-badge-${pnr.pnr_number}-${pIndex}`}
                              >
                                {getStatusIcon(passenger.current_status)}
                                {passenger.current_status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Confirmation Probability */}
                    <div className="mt-4 pt-4 border-t border-zinc-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-zinc-500">Confirmation Probability</span>
                        <span className="text-sm font-medium text-zinc-200">{pnr.confirmation_probability}%</span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full probability-bar transition-all duration-500"
                          style={{ width: `${pnr.confirmation_probability}%` }}
                        />
                      </div>
                    </div>

                    {pnr.calendar_event_id && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-emerald-400">
                        <Calendar className="w-3 h-3" />
                        <span>Synced to Google Calendar</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
          </main>
        </div>
      </div>

      {/* More Filters Dialog — opens the full PNRFilters UI (search,
          date range, class, sort) from the FilterRail "More filters"
          button. Internally URL-driven, so changes here flow back through
          the always-mounted hidden PNRFilters into filteredPnrs. */}
      <Dialog open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-heading">
              Advanced filters
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Filter by search query, journey date, class, or sort order.
              Changes persist in the URL so refreshing the page keeps your
              filter active.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <PNRFilters pnrs={pnrs} onFilteredChange={setFilteredPnrs} />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setMoreFiltersOpen(false)}
              className="text-zinc-300"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seat Chart Dialog */}
      <Dialog open={seatChartOpen} onOpenChange={setSeatChartOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>
              Seat Chart - Coach {selectedPnr?.passengers[0]?.coach || "S1"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedPnr?.train_name} ({selectedPnr?.train_number})
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[400px] pr-4">
            {loadingSeatChart ? (
              <div className="grid grid-cols-8 gap-2">
                {Array(72).fill(0).map((_, i) => (
                  <Skeleton key={i} className="w-8 h-8 rounded" />
                ))}
              </div>
            ) : seatChart ? (
              <div>
                {/* Legend */}
                <div className="flex items-center gap-4 mb-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-zinc-700" />
                    <span className="text-zinc-400">Booked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-emerald-500/20 border border-emerald-500/30" />
                    <span className="text-zinc-400">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-amber-500/20 border border-amber-500/30" />
                    <span className="text-zinc-400">RAC</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-indigo-500" />
                    <span className="text-zinc-400">Your Seat</span>
                  </div>
                </div>

                {/* Seat Grid */}
                <div className="grid grid-cols-8 gap-2 p-4 bg-zinc-800/30 rounded-lg">
                  {seatChart.seats.map((seat, index) => {
                    const isUserSeat = selectedPnr?.passengers.some(
                      p => p.seat_number === seat.number
                    );
                    
                    let seatClass = "seat ";
                    if (isUserSeat) {
                      seatClass += "seat-user";
                    } else if (seat.status === "booked") {
                      seatClass += "seat-booked";
                    } else if (seat.status === "rac") {
                      seatClass += "seat-rac";
                    } else {
                      seatClass += "seat-available";
                    }

                    // Add gap for aisle
                    const col = index % 8;
                    const marginClass = col === 6 ? "ml-4" : "";

                    return (
                      <div
                        key={seat.number}
                        className={`${seatClass} ${marginClass}`}
                        title={`Seat ${seat.number} - ${seat.position}`}
                        data-testid={`seat-${seat.number}`}
                      >
                        {seat.number}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 text-xs text-zinc-500">
                  <p>LB: Lower Berth • MB: Middle Berth • UB: Upper Berth • SL: Side Lower • SU: Side Upper</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                Failed to load seat chart
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Edit Passengers Dialog */}
      <Dialog open={editPassengersOpen} onOpenChange={setEditPassengersOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50" style={{ fontFamily: 'var(--font-heading)' }}>
              Edit Passenger Names
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              The IRCTC API doesn't provide passenger names for privacy. You can add them manually here.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editedPassengers.map((passenger, index) => (
              <div key={index} className="space-y-2">
                <Label className="text-zinc-300">Passenger {index + 1}</Label>
                <Input
                  value={passenger.name}
                  onChange={(e) => updatePassengerName(index, e.target.value)}
                  placeholder={`Enter name for Passenger ${index + 1}`}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  data-testid={`edit-passenger-name-${index}`}
                />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditPassengersOpen(false)}
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePassengers}
              disabled={savingPassengers}
              className="bg-indigo-500 hover:bg-indigo-400 text-white"
              data-testid="save-passengers-btn"
            >
              {savingPassengers ? (
                <RefreshCw className="w-4 h-4 mr-2 spinner" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Names
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Train Running Status Dialog */}
      {/* Live Train Status Dialog */}
      <Dialog open={liveStatusOpen} onOpenChange={setLiveStatusOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-50">
              <Radio className="w-5 h-5 text-sky-400" />
              Live Train Status
            </DialogTitle>
          </DialogHeader>
          {liveStatusPnrId && liveStatusOpen ? (
            <LiveTrainStatus
              pnrId={liveStatusPnrId}
              userId={user.id}
              onClose={() => setLiveStatusOpen(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>


      {/* Status Filter Modal */}
      <Dialog open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-xl text-zinc-50 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
              {statusFilter === 'confirmed' && <><CheckCircle className="w-5 h-5 text-emerald-400" /> Confirmed Passengers</>}
              {statusFilter === 'rac' && <><MinusCircle className="w-5 h-5 text-amber-400" /> RAC Passengers</>}
              {statusFilter === 'waiting' && <><XCircle className="w-5 h-5 text-rose-400" /> Waiting List Passengers</>}
              {statusFilter === 'cancelled' && <><AlertCircle className="w-5 h-5 text-red-400" /> Cancelled Passengers</>}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-3">
              {statusFilter && getFilteredPassengers(statusFilter).map((passenger, idx) => (
                <div 
                  key={idx} 
                  className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-zinc-100 font-medium">{passenger.name}</span>
                        <Badge variant="outline" className={getStatusColor(passenger.current_status)}>
                          {passenger.current_status}
                        </Badge>
                      </div>
                      <div className="text-sm text-zinc-400 space-y-1">
                        <p className="flex items-center gap-2">
                          <Train className="w-3 h-3" />
                          {passenger.train_name} ({passenger.train_number})
                        </p>
                        <p className="flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {passenger.boarding_station} → {passenger.destination_station}
                        </p>
                        <p className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {passenger.journey_date}
                        </p>
                        <p className="text-zinc-500">
                          PNR: <span className="font-mono">{passenger.pnr_number}</span>
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      {passenger.coach && (
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-300 border-zinc-700">
                          {passenger.coach}/{passenger.seat_number}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {statusFilter && getFilteredPassengers(statusFilter).length === 0 && (
                <div className="text-center py-8 text-zinc-500">
                  No passengers found
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
